import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, Button, Stack, Typography, Divider, InputAdornment } from '@mui/material';
import { useForm } from 'react-hook-form';

import { PaperCard } from 'components/Card';
import { FormProvider, TextField, NumericFormat, CardAutocomplete, Upload, MultiUpload } from 'components/Form';
import { InputWrapper } from 'components/Form/InputWrapper';
import { RadioGroupField } from 'components/Form/RadioGroupField';
import useYupValidationResolver from 'hook/useYupValidationResolver';
import { searchCompanyUsers } from 'features/companies/api/getCompanyUsers';
import { useAuth } from 'lib/auth';
import { applicationSchema } from '../validation';
import { useCreateApplication } from '../api/createApplication';

const UserItem = ({ full_name, ...rest }) => (
  <Stack gap={1} alignItems={'flex-start !important'} {...rest}>
    <Typography variant="body1" fontWeight="bold">
      {full_name}
    </Typography>
  </Stack>
);

const locationsPerColumn = 5;
const maxLocationColumns = 3;

const getLocationGridSx = (count) => {
  const columns = Math.min(maxLocationColumns, Math.ceil(count / locationsPerColumn));
  return {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: '1fr',
    gridTemplateRows: `repeat(${Math.ceil(count / columns)}, auto)`,
    columnGap: 2,
  };
};

const getDefaultValues = (user, places = []) => ({
  firstName: '',
  lastName: '',
  pricePerHour: '',
  cv: null,
  files: [],
  motivation: '',
  contactUserID: user?.user_id ?? null,
  placeIDs: places.map((place) => place.place_id),
  preferredPlaceID: places.length === 1 ? places[0].place_id : '',
});

export const ApplyForm = ({ jobAd, disabled, buttonSx = {} }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const places = useMemo(() => jobAd?.places ?? [], [jobAd]);

  const actingUser = useMemo(() => {
    if (!user) return null;
    const fullName = `${user.first_name} ${user.last_name}`;
    return { user_id: user.user_id, full_name: fullName, label: fullName };
  }, [user]);

  const methods = useForm({
    defaultValues: getDefaultValues(user, places),
    resolver: useYupValidationResolver(applicationSchema),
  });

  const handleOpen = () => {
    methods.reset(getDefaultValues(user, places));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    methods.reset(getDefaultValues(user, places));
  };

  const { mutate, isLoading } = useCreateApplication(handleClose);

  const onSubmit = (values) => {
    mutate({ jobAdID: jobAd.ad_id, values });
  };

  const handleDropCV = (acceptedFiles) => {
    if (acceptedFiles[0]) {
      methods.setValue('cv', acceptedFiles[0], { shouldValidate: true });
    }
  };

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const ApplyFormContent = (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100%',
        width: '100%',
        background: '#F3F4F6',
        justifyContent: 'center',
        display: 'flex',
        zIndex: (theme) => theme.zIndex.modal,
        alignItems: 'flex-start',
        overflow: 'hidden',
      }}
    >
      <Stack
        sx={{
          position: 'relative',
          width: '100%',
          height: `calc(100% - 69px)`,
          marginTop: 8.625,
          overflowY: 'auto',
          backgroundColor: '#fff',
        }}
      >
        <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          <PaperCard
            height={69}
            sx={{
              background: '#fff',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              px: 4,
              py: 2,
              border: 0,
              borderRadius: 0,
              boxShadow: 26,
              display: 'flex',
              boxSizing: 'border-box',
              zIndex: (theme) => theme.zIndex.modal + 1,
            }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Typography>Applying to ‘{jobAd?.headline}’</Typography>
              <Stack direction="row" gap={2}>
                <Button
                  size="medium"
                  onClick={handleClose}
                  variant="contained"
                  sx={({ palette }) => ({
                    width: 82,
                    boxShadow: 25,
                    color: palette.primary.main,
                    backgroundColor: '#fff',
                  })}
                >
                  Cancel
                </Button>
                <Button size="medium" type="submit" variant="contained" color="primary" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Apply'}
                </Button>
              </Stack>
            </Stack>
          </PaperCard>

          <Stack
            direction="row"
            width="100%"
            minHeight={`calc(100vh - 69px)`}
            height="100%"
            bgcolor={'#fff'}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Stack width={'50%'} direction="column" px={8} py={4}>
              <Stack direction={'row'} gap={2}>
                <TextField
                  name="firstName"
                  label="First name"
                  placeholder="Please enter First name"
                  required
                  type="text"
                  sx={{ mb: 2 }}
                  fullWidth
                />
                <TextField
                  name="lastName"
                  label="Last name"
                  placeholder="Please enter Last name"
                  required
                  type="text"
                  sx={{ mb: 2 }}
                  fullWidth
                />
              </Stack>
              <Stack direction={'row'} gap={2}>
                <NumericFormat
                  name="pricePerHour"
                  label="Price per hour"
                  placeholder="Please enter Price per hour"
                  type="input"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">SEK/h</InputAdornment>,
                  }}
                  sx={{ mb: 2 }}
                  wrapperSx={{ width: 1 }}
                  labelSx={{ mb: -0.5 }}
                  required
                />
                <CardAutocomplete
                  name="contactUserID"
                  label="Contact"
                  queryFunction={(search) => searchCompanyUsers(user?.company_id, search)}
                  optionLabelName="full_name"
                  ItemCard={UserItem}
                  onChange={(newValue) => {
                    methods.setValue('contactUserID', newValue ? newValue.user_id : null, { shouldValidate: true });
                  }}
                  value={actingUser}
                  skipCreatingShadow
                  fetchOnOpen
                  loadingInitial
                  changeSelectedValueOnChange
                  fullWidth
                  wrapperSx={{ mb: 2 }}
                />
              </Stack>
              {places.length > 1 && (
                <RadioGroupField
                  name="preferredPlaceID"
                  label="Preferred location"
                  required
                  row={false}
                  options={places.map((place) => ({ value: place.place_id, label: place.name }))}
                  sx={{ mb: 2, ...getLocationGridSx(places.length) }}
                />
              )}
              <TextField
                name="motivation"
                label="Motivation"
                placeholder="Please enter Motivation"
                type="text"
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiInputBase-root, .MuiInputBase-input': {
                    p: 0,
                  },
                }}
                multiline
                minRows={6}
              />
            </Stack>
            <Stack width={'50%'} direction="column" px={8} py={4} gap={2}>
              <InputWrapper label="CV" required fullWidth>
                <Upload name="cv" onDrop={handleDropCV} multiple={false} />
              </InputWrapper>
              <InputWrapper label="Additional files" fullWidth>
                <MultiUpload name="files" />
              </InputWrapper>
            </Stack>
          </Stack>
        </FormProvider>
      </Stack>
    </Box>
  );

  return (
    <>
      <Button size="medium" variant="contained" color="primary" sx={buttonSx} onClick={handleOpen} disabled={disabled}>
        Apply
      </Button>
      {open && ReactDOM.createPortal(ApplyFormContent, document.body)}
    </>
  );
};
